import React, {useState, useEffect} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR, {Fetcher} from 'swr';
import { api } from '../../api/api';
import { Layout, Card, Typography, Form, Space, Button, Table, message} from 'antd';
import type { TableProps } from 'antd';
import { PageHeader } from '@ant-design/pro-components';
import { CustomerForm } from '../components/CustomerForm';


//Interfaces copied here from the admin module of the backend.
interface Order {
    _id: string, 
    paymentStatus: string,
    status: string,
    createdAt: string,
    currency: string,
    itemTotal: number,
    shippingTotal: number,
    amountTotal: number,
    artworkId: string,
    artworkName: string,
    artworkImageLink: string,
    invoiceUrl?: string,
    hasInvoce: boolean,
    certificateUrl?: string,
    hasCertificates: boolean,
    number: number,
    lineItems: [{
        _id: string,
        price: number,
        quantity: number
    }]
}

interface CustomerWithOrders {
  _id: string,

  email: string,

  firstName?: string,
  lastName?: string,
  title?: string,

  phone?: {
      prefix?: string,
      number?: string
  },

  billingAddress?: {
      line1?: string,
      line2?: string,
      postalCode?: string,
      city?: string,
      state?: string,
      country?: string,
  },

  shippingAddress: {
      line1?: string,
      line2?: string,
      postalCode?: string,
      city?: string,
      state?: string,
      country?: string,
  },

  preferences?: {
      language?: string
  }

  orders: Order[];
}

const orederColumns: TableProps<Order>['columns'] = [
  {
    title: 'Order Number',
    dataIndex: 'number',
    key: 'number',
  },
  {
    title: 'Date',
    dataIndex: 'createdAt',
    key: 'created',
    render: (date: string) => (<span>{date.substring(0, 10)}</span>)
  },
  {
    title: 'Details',
    dataIndex: '_id',
    key: '_id',
    render: (_id: string) => {
      console.log("render:");
      console.log(_id);
      return (<a href={"/orders/detail/" + _id} target='_blank' rel="noopener noreferrer">Order details</a>)}
  },
  {
    title: 'Total',
    dataIndex: 'itemTotal',
    key: 'itemTotal',
    render: (text: any, record) => (
      <div>
        {record.amountTotal} {record.currency}<br></br>
        <small>
          <span>items:</span>{record.itemTotal}
          <span> shipping:</span>{record.shippingTotal}
        </small>
      </div>
    ),
  },
  {
    title: 'Payment',
    dataIndex: 'paymentStatus',
    key: 'paymentStatus',
  },
  {
    title: 'Attachments',
    dataIndex: 'paymentStatus',
    key: 'attachements',
    render: (_, record) => (
      <div>
        {record.invoiceUrl && (<a href={record.invoiceUrl} target='_blank' rel="noopener noreferrer">Invoice</a>)}
          <br />
        {record.certificateUrl && (<a href={record.certificateUrl} target='_blank' rel="noopener noreferrer">Certificate</a>)}
      </div>
    ),
  },
]

export const CustomerDetailPage = () => {
  //customer id
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate();
  //customer form
  const [customerForm] = Form.useForm();
  const [formDisabled, setFormDisabled] = useState(true);
  // form status
  const [isSaving, setIsSaving] = useState(false);

  //SWR data fetcher
  const fetcher: Fetcher<CustomerWithOrders, string> = async (url) => {
    const response = await api.get(url);
    console.log("Detail Data from admin api:", response.data);
    return response.data;
  };

  const { data: customer, error, isLoading, mutate } = useSWR<CustomerWithOrders>( //destructuring the returned object, and assigning the data field to a new variable called customer
    id ? `/admin/customers/${id}` : null, // SWR Key: Request only if 'id' exists
    fetcher
  );

  useEffect(() => {
    if (customer) {
      customerForm.setFieldsValue({
        title: customer.title,
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        preferences: customer.preferences, 
        phone: customer.phone,
        billingAddress: customer.billingAddress,
        shippingAddress: customer.shippingAddress,
      });
    }
  }, [customer, customerForm]);

  //Button handlers
  const handleFormEnableClick = () => {
    setFormDisabled(false); // Enable the form
  };

  const handleAddressCopyClick = () => {
    customerForm.setFieldsValue({
      billingAddress: customerForm.getFieldsValue(['shippingAddress'])['shippingAddress']
    });
  }

  const handleCancelClick = () => {
    setFormDisabled(true); // Disable the form
    // Reset form fields to original fetched data if available
    if (customer) {
      customerForm.setFieldsValue({
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        preferences: customer.preferences,
        phone: customer.phone,
        billingAddress: customer.billingAddress,
        shippingAddress: customer.shippingAddress,
      });
    }
  };

  const handleSaveClick = async () => {
    try {
      setIsSaving(true); 
      // Validate all fields in the form
      const values = await customerForm.validateFields();
      console.log('Form values validated:', values);

      // Prepare data for the API call 
      const updatePayload = {
        _id: id, // Send the ID for the update
        ...values, // Spread the validated form values
      };

      // Call your update API endpoint
      await api.put(`/admin/customers/${id}`, updatePayload);

      message.success('Customer updated successfully!');
      setFormDisabled(true);

      //Optimistic revaliadtion, without request to server
      const updatedCustomer : CustomerWithOrders = {
        ...customer, //original customer
        ...updatePayload, //overwrite updated fields
        _id: customer!._id
      }
      mutate(updatedCustomer, false);

    } catch (error: any) {
      console.error('Failed to update customer:', error);
      if (error.response && error.response.data && error.response.data.message) {
        let errorMessage = error.response.data.message;
        
        if(Array.isArray(errorMessage)){
          //handle validation error
          message.error(`Invalid field: ${errorMessage.map(el => {return el.property})}`);
        }
        else{
          message.error(`Failed to update customer: ${errorMessage}`);
        }
      } else {
        message.error('Failed to update customer. Please try again.');
      }
    } finally {
      setIsSaving(false); // Reset saving state
    }
  };

  return (
    <Layout.Content>
      <PageHeader 
        onBack={() => navigate(-1)}
        title={'Customer ' + (customer? `: ${customer.firstName} ${customer.lastName}` : '')}
        extra={
          formDisabled ? (
            <Button type="primary" onClick={handleFormEnableClick}>
              Edit
            </Button>
          ) : (
            <Space>
              <Button type="default" onClick={handleCancelClick}>Cancel</Button>
              <Button type="primary" onClick={handleSaveClick} loading={isSaving}>
                Save
              </Button>
            </Space>
          )
        }>
      </PageHeader>
    
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Typography.Title level={4}>Personal data</Typography.Title>

          <CustomerForm customerForm={customerForm} formDisabled={formDisabled} 
            copyAddressCallback={handleAddressCopyClick}/>
          
        </Card>

        <Card>
          <Typography.Title level={4}>Orders</Typography.Title>
          <Table
            columns={orederColumns}
            dataSource={customer?.orders}
            loading={isLoading}
            rowKey={"_id"}
          />
          
        </Card>
      </Space>

    </Layout.Content>
  );
};
